// Shared validation for portfolio accounts, used by the create form and the API.
import { z } from "zod";

export const accountSchema = z.object({
  label: z.string().trim().min(1, "Ange ett namn").max(60),
  avgCost: z.number().nonnegative("GAV kan inte vara negativt"),
  shares: z.number().positive("Antal aktier måste vara större än 0"),
});

export const accountsSchema = z
  .array(accountSchema)
  .min(1, "Lägg till minst ett konto");

export const createPortfolioSchema = z.object({
  accounts: accountsSchema,
});

export type AccountInput = z.infer<typeof accountSchema>;

// ---- Form variants ----
// Number inputs hand us strings; coerce them so empty/invalid values produce
// friendly field errors instead of "Expected number, received nan".
const numberField = (msg: string) =>
  z.coerce.number({ invalid_type_error: "Ogiltigt tal", required_error: msg });

export const accountFormSchema = z.object({
  label: z.string().trim().min(1, "Ange ett namn").max(60),
  avgCost: numberField("Ange GAV").nonnegative("GAV kan inte vara negativt"),
  shares: numberField("Ange antal").positive("Antal aktier måste vara större än 0"),
});

export const portfolioFormSchema = z.object({
  accounts: z.array(accountFormSchema).min(1, "Lägg till minst ett konto"),
});

/** What the form fields hold before coercion (raw input strings). */
export type AccountFormValue = { label: string; avgCost: string; shares: string };
export type PortfolioFormValues = { accounts: AccountFormValue[] };

/** Convert validated form rows to the numeric API shape. */
export function toAccountInputs(accounts: AccountFormValue[]): AccountInput[] {
  return accounts.map((a) => ({
    label: a.label.trim(),
    avgCost: Number(a.avgCost),
    shares: Number(a.shares),
  }));
}
